
import React from 'react';
import RecommendationCard from './RecommendationCard';
import { Droplet, ShowerHead, Droplets, Thermometer, Wind } from 'lucide-react';
import { useUserReport } from '@/hooks/useUserReport';

const WaterAndHeatingRecommendations = () => {
  const { data: report } = useUserReport();

  const handleClick = (link: string | undefined) => {
    if (link) {
      window.open(link, '_blank');
    }
  };

  const formatLitres = (value: number | undefined | null) => {
    if (value === undefined || value === null) return '0';
    return value.toLocaleString('en-GB');
  };

  const formatSwimmingPools = (value: number | undefined | null) => {
    if (value === undefined || value === null) return '0.0';
    return value.toFixed(1);
  };

  return (
    <section className="w-full py-8">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 space-y-8">
        {/* Water Consumption Section */}
        <div className="w-full bg-[#ECFDF5] shadow-lg rounded-xl p-6">
          <h2 className="font-bold text-2xl text-[#1F2937] mb-6">
            Reducing Water Consumption
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <RecommendationCard
              title="Low-Flow Showerhead"
              category="Water Saving"
              annualSaving={report?.showerhead_sav || 0}
              oneTimeCost={report?.showerhead_cost || 0}
              icon={<ShowerHead className="w-5 h-5" />}
              onBuyClick={() => handleClick(report?.showerhead_link)}
            />

            <RecommendationCard
              title="Cistern Reduction"
              category="Water Saving"
              annualSaving={report?.cistern_sav || 0}
              oneTimeCost={report?.cistern_cost || 0}
              icon={<Droplet className="w-5 h-5" />}
              onBuyClick={() => handleClick(report?.cistern_link)}
            />

            <RecommendationCard
              title="Taps"
              category="Water Saving"
              annualSaving={report?.taps_sav || 0}
              oneTimeCost={report?.taps_cost || 0}
              icon={<Droplets className="w-5 h-5" />}
              onBuyClick={() => handleClick(report?.taps_link)}
            />
          </div>

          <div className="mt-6 text-center font-bold text-2xl text-[#1F2937]">
            These changes will save {formatLitres(report?.litres_saved)} litres per year, equivalent to {formatSwimmingPools(report?.swimming_pools)} swimming pools!
          </div>
        </div>

        {/* Heat Retention Section */}
        <div className="w-full bg-[#ECFDF5] shadow-lg rounded-xl p-6">
          <h2 className="font-bold text-2xl text-[#1F2937] mb-6">
            Improving Heat Retention
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <RecommendationCard
              title="Water Tank Insulation"
              category="Heat retention"
              annualSaving={report?.insulation_sav || 0}
              oneTimeCost={report?.insulation_cost || 0}
              icon={<Thermometer className="w-5 h-5" />}
              onBuyClick={() => handleClick(report?.insulation_link)}
            />
          </div>
        </div>

        {/* Draft-Proofing Section */}
        <div className="w-full bg-[#ECFDF5] shadow-lg rounded-xl p-6">
          <h2 className="font-bold text-2xl text-[#1F2937] mb-6">
            Draft-Proofing
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <RecommendationCard
              title="Chimney Draft-Proofer"
              category="Draftsproofer"
              annualSaving={report?.chimney_sav || 0}
              oneTimeCost={report?.chimney_cost || 0}
              icon={<Wind className="w-5 h-5" />}
              onBuyClick={() => handleClick(report?.chimney_link)}
            />

            <RecommendationCard
              title="Draft Tape"
              category="Draftproofer"
              annualSaving={report?.draft_sav || 0}
              oneTimeCost={report?.draft_cost || 0}
              icon={<Wind className="w-5 h-5" />}
              onBuyClick={() => handleClick(report?.draft_link)}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default WaterAndHeatingRecommendations;
